import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { MdPeople } from "react-icons/md";
import Card from "components/card";
import Badge from "components/badge/Badge";
import Avatar from "components/avatar";
import Button from "components/button";
import Skeleton from "components/skeleton/Skeleton";
import { PageContainer, PageHeader } from "components/page";
import { EmptyState, ErrorState } from "components/states";
import { SearchInput, Select } from "components/fields";
import { getAudience, profilePath } from "components/assessment/audiences";
import { ARCHETYPE_KEYS, ARCHETYPES, getArchetype } from "lib/archetypes";
import { getApiErrorMessage } from "api";
import { formatDate } from "utils/format";

const ARCHETYPE_OPTIONS = [
  { value: "", label: "All Archetypes" },
  ...ARCHETYPE_KEYS.map((key) => ({ value: key, label: ARCHETYPES[key].shortLabel })),
];

// Values are user roles from the API, not audience copy.
const ROLE_OPTIONS = [
  { value: "", label: "All Roles" },
  { value: "staff", label: "Staff" },
  { value: "volunteer", label: "Volunteer" },
  { value: "admin", label: "Admin" },
  { value: "elder", label: "Elder" },
];

const SORT_OPTIONS = [
  { value: "name", label: "Sort: Name" },
  { value: "archetype", label: "Sort: Archetype" },
  { value: "date", label: "Sort: Recent" },
];

const ArchetypeBadge = ({ style }) => {
  if (!getArchetype(style)) return <Badge>Not Assessed</Badge>;
  return <Badge tone="archetype" archetype={style} />;
};

const ProfileCard = ({ profile, config }) => (
  <Card
    as={Link}
    to={profilePath(config, profile.userId)}
    padding="md"
    className="focus-ring transition-colors hover:bg-surface-raised"
  >
    <div className="flex items-start gap-4">
      <Avatar user={{ avatar: profile.avatar }} name={profile.name} size="lg" />
      <div className="min-w-0 flex-1">
        <p className="truncate font-medium text-fg">{profile.name}</p>
        <p className="text-xs capitalize text-fg-muted">{profile.role || config.labels.role}</p>
        <div className="mt-2 flex flex-wrap gap-1.5">
          <ArchetypeBadge style={profile.primaryStyle} />
          {profile.secondaryStyle && (
            <Badge>{getArchetype(profile.secondaryStyle)?.shortLabel || profile.secondaryStyle}</Badge>
          )}
        </div>
      </div>
    </div>
    {profile.lastAssessmentAt && (
      <p className="mt-3 text-xs text-fg-muted">Assessed {formatDate(profile.lastAssessmentAt)}</p>
    )}
  </Card>
);

const sortProfiles = (sortBy) => (a, b) => {
  if (sortBy === "name") return (a.name || "").localeCompare(b.name || "");
  if (sortBy === "archetype") return (a.primaryStyle || "").localeCompare(b.primaryStyle || "");
  if (sortBy === "date") return new Date(b.lastAssessmentAt || 0) - new Date(a.lastAssessmentAt || 0);
  return 0;
};

export default function Profiles({ audience }) {
  const config = getAudience(audience);
  const { labels } = config;
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [profiles, setProfiles] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [filterArchetype, setFilterArchetype] = useState(searchParams.get("archetype") || "");
  const [filterRole, setFilterRole] = useState("");
  const [sortBy, setSortBy] = useState("name");

  const fetchProfiles = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await config.service.getProfiles();
      if (response.success) {
        setProfiles(response.data || []);
      }
    } catch (err) {
      setError(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProfiles();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [audience]);

  const clearFilters = () => {
    setSearchQuery("");
    setFilterArchetype("");
    setFilterRole("");
  };

  const filtered = profiles
    .filter((p) => {
      if (searchQuery) {
        const q = searchQuery.toLowerCase();
        const name = (p.name || "").toLowerCase();
        const email = (p.email || "").toLowerCase();
        if (!name.includes(q) && !email.includes(q)) return false;
      }
      if (filterArchetype && p.primaryStyle !== filterArchetype) return false;
      if (filterRole && p.role !== filterRole) return false;
      return true;
    })
    .sort(sortProfiles(sortBy));

  const renderResults = () => {
    if (loading) {
      return (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
          {Array.from({ length: 6 }, (_, i) => (
            <Skeleton key={i} className="h-36 w-full rounded-card" />
          ))}
        </div>
      );
    }
    if (error) {
      return <ErrorState message={getApiErrorMessage(error)} onRetry={fetchProfiles} />;
    }
    if (profiles.length === 0) {
      return (
        <Card padding="md">
          <EmptyState
            icon={MdPeople}
            title="No Profiles Yet"
            description={`No ${labels.adjective.toLowerCase()} profiles have been created yet.`}
          />
        </Card>
      );
    }
    if (filtered.length === 0) {
      return (
        <Card padding="md">
          <EmptyState
            variant="filtered"
            description="Try adjusting your search or filters."
            action={
              <Button variant="ghost" onClick={clearFilters}>
                Clear Filters
              </Button>
            }
          />
        </Card>
      );
    }
    return (
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
        {filtered.map((profile) => (
          <ProfileCard key={profile.userId || profile.id} profile={profile} config={config} />
        ))}
      </div>
    );
  };

  return (
    <PageContainer>
      <PageHeader
        title="Profiles"
        description={`${labels.adjective} profiles with ministry archetype insights`}
      />

      <Card padding="sm">
        <div className="flex flex-col gap-3 md:flex-row md:items-center">
          <SearchInput
            value={searchQuery}
            onChange={setSearchQuery}
            placeholder="Search by name or email…"
            ariaLabel="Search profiles"
            className="flex-1"
          />
          <div className="flex flex-col gap-3 sm:flex-row">
            <Select
              aria-label="Filter by archetype"
              className="w-full sm:w-44"
              value={filterArchetype}
              onChange={(e) => setFilterArchetype(e.target.value)}
              options={ARCHETYPE_OPTIONS}
            />
            <Select
              aria-label="Filter by role"
              className="w-full sm:w-36"
              value={filterRole}
              onChange={(e) => setFilterRole(e.target.value)}
              options={ROLE_OPTIONS}
            />
            <Select
              aria-label="Sort profiles"
              className="w-full sm:w-40"
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              options={SORT_OPTIONS}
            />
          </div>
        </div>
      </Card>

      {renderResults()}
    </PageContainer>
  );
}
